/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useMemo } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import SignUp from './components/auth/SignUp';
import ForgotPassword from './components/auth/ForgotPassword';
import SignIn from './components/auth/SignIn';
import Home from './components/user/Home';
import Navbar from './components/layout/Navbar';
import ProtectedRoute from './components/common/ProtectedRoute';
// import SavedApplications from './components/shared/SavedApplications';
// import ExtendRevise from './components/user/ExtendRevise';
// import History from './components/user/History';

import ManageProgramData from './components/admin/ManageProgramData';
import ApplicationReview from './components/admin/ApplicationReview';
import Reporting from './components/admin/Reporting';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { BrandingProvider, useBranding } from './contexts/BrandingContext';
import { deriveBrandTokens } from './utils/brandColors';
import './styles/App.css';
import axios from 'axios';
import Applications from './components/user/Applications';
import EditApplication from './components/user/EditApplication';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Projects from './components/shared/Projects';
import UnifiedProjects from './components/user/UnifiedProjects';
import Requests from './components/user/requests/Requests';
import Invoices from './components/shared/Invoices';
import ProjectView from './components/shared/ProjectView';
import RequestManagement from './components/admin/RequestManagement';
import RequestReview from './components/admin/RequestReview';
import UserProfile from './components/user/UserProfile';
import './utils/dateConfig';
import Accounting from './components/admin/Accounting';
import AdminChats from './components/admin/AdminChats';
import FacilityChats from './components/user/FacilityChats';
import UserRevisionReview from './components/shared/UserRevisionReview';
import { ModuleGate, ModuleDisabled } from './modules/registry';
import IpmModule from './modules/ipm/IpmModule';

const queryClient = new QueryClient();


const AppRoutes = () => {
  const { user } = useAuth();

  return (
    <NotificationProvider>
      {/* First stop for a keyboard user: jump straight past the navigation. */}
      <a href="#main-content" className="skip-to-content">Skip to main content</a>
      {user && <Navbar user={user} />}
      {/* The single <main> landmark screen readers navigate to. */}
      <main id="main-content" tabIndex={-1}>
      <Routes>
        <Route path="/signin" element={<SignIn />} />
        <Route path="/signup" element={<SignUp />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/" element={<ProtectedRoute><Home user={user} /></ProtectedRoute>} />
        
        {/* Projects routes (unified applications and projects) */}
        <Route 
          path="/projects" 
          element={
            <ProtectedRoute>
              <UnifiedProjects user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/projects/:section" 
          element={
            <ProtectedRoute>
              <UnifiedProjects user={user} />
            </ProtectedRoute>
          } 
        />
        <Route path="/projects/view/:id" element={<ProtectedRoute><ProjectView user={user} /></ProtectedRoute>} />
        <Route path="/projects/revise/:id" element={<ProtectedRoute><UserRevisionReview user={user} /></ProtectedRoute>} />

        {/* Legacy Applications routes (for backward compatibility) */}
        <Route 
          path="/applications/*" 
          element={
            <ProtectedRoute>
              <Applications user={user} />
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/applications/edit/:id" 
          element={
            <ProtectedRoute>
              <EditApplication user={user} />
            </ProtectedRoute>
          } 
        />

        {/* Admin routes */}
        <Route 
          path="/admin/manage-program-data" 
          element={<ProtectedRoute adminOnly><ManageProgramData user={user} /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/application" 
          element={<ProtectedRoute adminOnly><ApplicationReview user={user} /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/reporting" 
          element={<ProtectedRoute adminOnly><Reporting user={user} /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/requests" 
          element={<ProtectedRoute adminOnly><RequestManagement user={user} /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/requests/:id" 
          element={<ProtectedRoute adminOnly><RequestReview user={user} /></ProtectedRoute>} 
        />
        <Route 
          path="/admin/accounting" 
          element={
            <ProtectedRoute adminOnly={true}>
              <Accounting />
            </ProtectedRoute> 
          } 
        />

        <Route
          path="/admin/chats"
          element={<ProtectedRoute adminOnly><AdminChats user={user} /></ProtectedRoute>}
        />

        {/* Optional modules (src/modules) */}
        <Route
          path="/admin/ipm"
          element={<ProtectedRoute adminOnly><ModuleGate module="ipm" fallback={<ModuleDisabled />}><IpmModule /></ModuleGate></ProtectedRoute>}
        />

        {/* User routes */}
        <Route path="/home" element={<Home />} />
        <Route 
          path="/profile" 
          element={<ProtectedRoute><UserProfile /></ProtectedRoute>} 
        />
        <Route
          path="/chats"
          element={<ProtectedRoute><FacilityChats user={user} /></ProtectedRoute>}
        />
        
        {/* Legacy Projects routes (keeping for backward compatibility) */}
        <Route path="/legacy-projects" element={<ProtectedRoute><Projects /></ProtectedRoute>}>
          <Route index element={<Projects />} />
          <Route path="past" element={<Projects />} />
          <Route path="extend" element={<Projects />} />
        </Route>
        <Route path="/projects/requests/*" element={<ProtectedRoute><Requests /></ProtectedRoute>} />

        <Route path="/invoices" element={<Invoices />} />
      </Routes>
      </main>
    </NotificationProvider>
  );
};

/**
 * Feeds the admin-chosen theme colour into MUI, so the hundreds of Buttons,
 * Tabs, Chips and Switches across the app follow the brand without any of them
 * naming a colour themselves.
 *
 * `contrastText` comes from the derived tokens rather than MUI's own guess,
 * which keeps button labels readable on a light brand colour.
 */
const BrandedThemeProvider = ({ children }) => {
  const { branding } = useBranding();

  const theme = useMemo(() => {
    const tokens = deriveBrandTokens(branding.primary_color);
    return createTheme({
      palette: {
        primary: {
          main: tokens.primary,
          dark: tokens['primary-dark'],
          light: tokens['primary-light'],
          contrastText: tokens['on-primary']
        },
        action: {
          // Every MUI hover — menu items, table rows, list items, icon buttons,
          // autocomplete options — reads this one value, so a faint tint of the
          // brand colour here matches the hovers the stylesheets use.
          hover: tokens['primary-softer']
        }
      },
      components: {
        // A visible keyboard focus ring everywhere, in the brand colour.
        MuiButtonBase: {
          styleOverrides: {
            root: {
              '&.Mui-focusVisible': {
                outline: `3px solid ${tokens['focus-ring']}`,
                outlineOffset: '2px'
              }
            }
          }
        }
      }
    });
  }, [branding.primary_color]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline enableColorScheme />
      {children}
    </ThemeProvider>
  );
};

const App = () => {
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        await axios.get('/api/users/current');
      } catch (error) {
        console.error('Error fetching user:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchUser();
  }, []);

  if (loading) {
    return <div>Loading...</div>;
  }

  return (
    <QueryClientProvider client={queryClient}>
      {/* Branding sits outside AuthProvider: the sign-in page needs the logo,
          name and theme colour before anyone has authenticated. */}
      <BrandingProvider>
        <BrandedThemeProvider>
          <AuthProvider>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Router>
                <ToastContainer position="top-right" autoClose={3000} />
                <AppRoutes />
              </Router>
            </LocalizationProvider>
          </AuthProvider>
        </BrandedThemeProvider>
      </BrandingProvider>
    </QueryClientProvider>
  );
};

export default App;
